package navigation.settings.categoriestags

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import cafe.adriel.voyager.koin.koinScreenModel
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.category.ui.addeditcategory.AddEditCategoryEvent
import com.essntl.features.category.ui.addeditcategory.components.AddEditCategoryDialog
import com.essntl.features.category.ui.allcategories.AllCategoriesEvent
import com.essntl.features.category.ui.allcategories.AllCategoriesNavigation
import com.essntl.features.category.ui.allcategories.AllCategoriesScreen
import com.essntl.features.tag.domain.model.TagModel
import com.essntl.features.tag.ui.addedittag.AddEditTagEvent
import com.essntl.features.tag.ui.addedittag.components.AddEditTagDialog
import com.essntl.features.tag.ui.alltags.AllTagsEvent
import com.essntl.features.tag.ui.alltags.AllTagsNavigation
import com.essntl.features.tag.ui.alltags.AllTagsScreen
import com.essntl.uikit.components.utils.spacing
import navigation.settings.categories.addeditcategory.AddEditCategoryScreenModel
import navigation.settings.categories.allcategories.AllCategoriesScreenModel
import navigation.settings.tags.addedittag.AddEditTagScreenModel
import navigation.settings.tags.alltags.AllTagsScreenModel
import navigation.utils.NavLinkScreen

class CategoriesTagsScreenNav(
    private val onRefresh: (() -> Unit) -> Unit,
) : NavLinkScreen {

    override val defaultTitle: String get() = "Categories & Tags"

    @Composable
    override fun Content() {
        val allCategoriesScreenModel = koinScreenModel<AllCategoriesScreenModel>()
        val allCategoriesState by allCategoriesScreenModel.state.collectAsState()

        val addEditCategoryScreenModel = koinScreenModel<AddEditCategoryScreenModel>()
        val addEditCategoryState by addEditCategoryScreenModel.state.collectAsState()

        var openAddEditCategoryDialog by rememberSaveable { mutableStateOf(false) }
        var categoryToEdit by remember { mutableStateOf<CategoryModel?>(null) }

        val allTagsScreenModel = koinScreenModel<AllTagsScreenModel>()
        val allTagsState by allTagsScreenModel.state.collectAsState()

        val addEditTagScreenModel = koinScreenModel<AddEditTagScreenModel>()
        val addEditTagState by addEditTagScreenModel.state.collectAsState()

        var openAddEditTagDialog by rememberSaveable { mutableStateOf(false) }
        var tagToEdit by remember { mutableStateOf<TagModel?>(null) }

        LaunchedEffect(Unit) {
            allCategoriesScreenModel.onEvent(AllCategoriesEvent.GetAllCategories)
            allTagsScreenModel.onEvent(AllTagsEvent.GetAllTags)
            onRefresh {
                allCategoriesScreenModel.onEvent(AllCategoriesEvent.GetAllCategories)
                allTagsScreenModel.onEvent(AllTagsEvent.GetAllTags)
            }
        }

        LaunchedEffect(addEditCategoryState.addEditCategory.isSuccess) {
            if (addEditCategoryState.addEditCategory.isSuccess) {
                addEditCategoryState.addEditCategory.data?.let {
                    if (categoryToEdit == null) {
                        allCategoriesScreenModel.onEvent(AllCategoriesEvent.AddCategory(it))
                    } else {
                        allCategoriesScreenModel.onEvent(AllCategoriesEvent.EditCategory(it))
                    }
                }
                addEditCategoryScreenModel.onEvent(AddEditCategoryEvent.ClearState)
                openAddEditCategoryDialog = false
                categoryToEdit = null
            }
        }

        LaunchedEffect(addEditCategoryState.deleteCategory.isSuccess) {
            if (addEditCategoryState.deleteCategory.isSuccess) {
                categoryToEdit?.let {
                    allCategoriesScreenModel.onEvent(AllCategoriesEvent.DeleteCategory(it))
                    addEditCategoryScreenModel.onEvent(AddEditCategoryEvent.ClearState)
                    openAddEditCategoryDialog = false
                    categoryToEdit = null
                }
            }
        }

        LaunchedEffect(addEditTagState.addEditTag.isSuccess) {
            if (addEditTagState.addEditTag.isSuccess) {
                addEditTagState.addEditTag.data?.let {
                    if (tagToEdit == null) {
                        allTagsScreenModel.onEvent(AllTagsEvent.AddTag(it))
                    } else {
                        allTagsScreenModel.onEvent(AllTagsEvent.EditTag(it))
                    }
                }
                addEditTagScreenModel.onEvent(AddEditTagEvent.ClearState)
                openAddEditTagDialog = false
                tagToEdit = null
            }
        }

        LaunchedEffect(addEditTagState.deleteTag.isSuccess) {
            if (addEditTagState.deleteTag.isSuccess) {
                tagToEdit?.let {
                    allTagsScreenModel.onEvent(AllTagsEvent.DeleteTag(it))
                    addEditTagScreenModel.onEvent(AddEditTagEvent.ClearState)
                    openAddEditTagDialog = false
                    tagToEdit = null
                }
            }
        }

        Column(
            verticalArrangement = Arrangement.spacedBy(3.spacing),
            modifier = Modifier.fillMaxSize(),
        ) {
            AllCategoriesScreen(
                state = allCategoriesState,
                onEvent = allCategoriesScreenModel::onEvent,
                onNavigation = {
                    when (it) {
                        is AllCategoriesNavigation.AddCategory -> {
                            openAddEditCategoryDialog = true
                        }

                        is AllCategoriesNavigation.EditCategory -> {
                            categoryToEdit = it.category
                            openAddEditCategoryDialog = true
                        }
                    }
                },
            )

            AllTagsScreen(
                state = allTagsState,
                onEvent = allTagsScreenModel::onEvent,
                onNavigation = {
                    when (it) {
                        is AllTagsNavigation.AddTag -> {
                            openAddEditTagDialog = true
                        }

                        is AllTagsNavigation.EditTag -> {
                            tagToEdit = it.tag
                            openAddEditTagDialog = true
                        }
                    }
                },
            )
        }

        AddEditCategoryDialog(
            openDialog = openAddEditCategoryDialog,
            currentCategory = categoryToEdit,
            state = addEditCategoryState,
            validation = addEditCategoryScreenModel.validation,
            onEvent = addEditCategoryScreenModel::onEvent,
            onDismissRequest = {
                openAddEditCategoryDialog = false
                categoryToEdit = null
            },
        )

        AddEditTagDialog(
            openDialog = openAddEditTagDialog,
            tag = tagToEdit,
            state = addEditTagState,
            validation = addEditTagScreenModel.validation,
            onEvent = addEditTagScreenModel::onEvent,
            onDismissRequest = {
                openAddEditTagDialog = false
                tagToEdit = null
            },
        )
    }
}